import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Banknote,
  CalendarDays,
  FileText,
  IndianRupee,
  Layers,
  Loader2,
  Package,
  Pencil,
  RefreshCw,
  Save,
  Search,
  TrendingUp,
  Users,
  Rocket,
  BookOpen,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import DatePickerField from "../../components/common/DatePickerField";
import {
  getTemplates,
  assignEmployeeSalary,
  listEmployeeSalaryStructures,
  getEmployeeSalaryStructure,
} from "./SalaryApi";
import { getEmployee } from "../Employees/EmployeeApi";
import EmployeeSelect from "../../components/common/EmployeeSelect";
import { findEmployeeById, formatEmployeeLabel } from "../../components/common/employeeUtils";
import { useEmployees } from "../../hooks/useEmployees";
import { useToast } from "../Context/ToastContext";
import {
  HrHero,
  HrPanel,
  HrBtn,
  HrInput,
  HrSelect,
  HrEmptyState,
} from "../../components/hr/HrDesignSystem";
import PayrollSetupProgress from "./v2/enterprise/components/PayrollSetupProgress";
import "../../styles/IndianCalendar.css";

function getTemplateStatus(t) {
  const raw = String(t?.status || t?.meta?.status || "").trim().toLowerCase();
  if (raw === "draft" || raw === "inactive" || raw === "active") return raw;
  if (t?.is_active === false) return "inactive";
  return "active";
}

function isTemplateAssignable(t) {
  return getTemplateStatus(t) !== "inactive";
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarHue(name) {
  let hash = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  const hues = ["#4f46e5", "#0f766e", "#0369a1", "#7c3aed", "#c2410c", "#15803d"];
  return hues[Math.abs(hash) % hues.length];
}

function resolveProfileDefaults(emp) {
  if (!emp) return { ctc: "", effectiveFrom: todayIso() };
  const ctc =
    emp.annual_ctc != null && emp.annual_ctc !== "" ? String(emp.annual_ctc) : "";
  const effectiveFrom = emp.date_of_joining
    ? String(emp.date_of_joining).slice(0, 10)
    : todayIso();
  return { ctc, effectiveFrom };
}

/** Prefer live employee list, then API-enriched name — never show a bare UUID. */
function structureEmployeeLabel(structure, employees) {
  const emp = findEmployeeById(employees, structure?.employee_id);
  const fromList = formatEmployeeLabel(emp);
  if (fromList) return fromList;
  const name = String(structure?.employee_name || "").trim();
  const code = structure?.employee_code ? ` (${structure.employee_code})` : "";
  if (name) return `${name}${code}`;
  return "Unknown employee";
}

/** One row per employee_id (latest effective_from wins). */
function dedupeStructuresByEmployee(rows) {
  const map = new Map();
  for (const row of rows || []) {
    const key = String(row?.employee_id || "");
    if (!key) continue;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, row);
      continue;
    }
    const prevDate = String(prev.effective_from || "");
    const nextDate = String(row.effective_from || "");
    if (nextDate > prevDate || (nextDate === prevDate && String(row.id) > String(prev.id))) {
      map.set(key, row);
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    String(b.effective_from || "").localeCompare(String(a.effective_from || ""))
  );
}

export default function EmployeeSalaryStructure() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { employees, refresh: refreshEmployees, loading: employeesLoading, error: employeesError } = useEmployees();

  useEffect(() => {
    refreshEmployees();
  }, [refreshEmployees]);
  const [templates, setTemplates] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [setupRefreshKey, setSetupRefreshKey] = useState(0);

  const [employeeId, setEmployeeId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [ctc, setCtc] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [ctcPrefillSource, setCtcPrefillSource] = useState(null);
  const loadedPackageFor = useRef("");
  const dateDirty = useRef(false);

  function isoDate(value) {
    const text = String(value || "").trim();
    return text ? text.slice(0, 10) : "";
  }

  useEffect(() => {
    loadTemplates();
    loadStructures();
  }, []);

  useEffect(() => {
    const id = searchParams.get("employee_id") || searchParams.get("employee");
    if (id) setEmployeeId(String(id));
    const tpl = searchParams.get("template_id");
    if (tpl) setTemplateId(String(tpl));
  }, [searchParams]);

  useEffect(() => {
    if (!employeeId) {
      loadedPackageFor.current = "";
      dateDirty.current = false;
      setTemplateId("");
      setCtc("");
      setEffectiveFrom(todayIso());
      setCtcPrefillSource(null);
      return undefined;
    }
    const key = String(employeeId);
    if (loadedPackageFor.current === key) return undefined;
    loadedPackageFor.current = key;
    dateDirty.current = false;
    let cancelled = false;
    (async () => {
      setLoadingCurrent(true);
      setCtcPrefillSource(null);
      try {
        let current = null;
        try {
          current = await getEmployeeSalaryStructure(employeeId);
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
        }

        if (cancelled) return;

        if (current?.ctc != null && current.ctc !== "") {
          setTemplateId(current.template_id || "");
          setCtc(String(current.ctc));
          if (!dateDirty.current) {
            setEffectiveFrom(isoDate(current.effective_from) || todayIso());
          }
          setCtcPrefillSource("package");
          return;
        }

        const urlCtc = searchParams.get("ctc");
        const fromList = findEmployeeById(employees, employeeId);
        let profile = fromList;
        if (!profile?.annual_ctc) {
          try {
            profile = await getEmployee(employeeId);
          } catch {
            profile = fromList;
          }
        }

        if (cancelled) return;

        const { ctc: profileCtc, effectiveFrom: profileDate } = resolveProfileDefaults(profile);
        const nextCtc = urlCtc || profileCtc;

        setTemplateId("");
        setCtc(nextCtc);
        if (!dateDirty.current) setEffectiveFrom(profileDate);
        if (nextCtc) {
          setCtcPrefillSource(urlCtc ? "wizard" : "profile");
        }
      } catch {
        if (!cancelled && !dateDirty.current) {
          setTemplateId("");
          setCtc("");
          setEffectiveFrom(todayIso());
          setCtcPrefillSource(null);
        }
      } finally {
        if (!cancelled) setLoadingCurrent(false);
      }
    })();
    return () => {
      cancelled = true;
      if (loadedPackageFor.current === key) loadedPackageFor.current = "";
    };
    // Reload only when the employee changes. Refreshing the employee list
    // must not put the saved assignment date back over an edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  async function loadTemplates() {
    const data = await getTemplates();
    setTemplates(Array.isArray(data) ? data : []);
  }

  async function loadStructures() {
    setLoadingList(true);
    try {
      const data = await listEmployeeSalaryStructures();
      setStructures(dedupeStructuresByEmployee(Array.isArray(data) ? data : []));
      setSetupRefreshKey((k) => k + 1);
    } finally {
      setLoadingList(false);
    }
  }

  function beginEditPackage(structure) {
    if (!structure?.employee_id) return;
    setEmployeeId(String(structure.employee_id));
    setTemplateId(structure.template_id ? String(structure.template_id) : "");
    setCtc(structure.ctc != null && structure.ctc !== "" ? String(structure.ctc) : "");
    setEffectiveFrom(isoDate(structure.effective_from) || todayIso());
    setCtcPrefillSource("package");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const canSave = Boolean(
    employeeId &&
      templateId &&
      ctc &&
      effectiveFrom &&
      !saving &&
      isTemplateAssignable(templates.find((t) => t.template_id === templateId))
  );

  const monthlyCtc = useMemo(() => {
    const n = Number(ctc);
    return Number.isFinite(n) && n > 0 ? Math.round(n / 12) : null;
  }, [ctc]);

  const assignableTemplates = useMemo(() => {
    return templates.filter((t) => isTemplateAssignable(t) || t.template_id === templateId);
  }, [templates, templateId]);

  const selectedTemplate = templates.find((t) => t.template_id === templateId);
  const selectedIsInactive = Boolean(selectedTemplate && !isTemplateAssignable(selectedTemplate));
  const selectedEmployee = findEmployeeById(employees, employeeId);

  const stats = useMemo(() => {
    const totalCtc = structures.reduce((sum, s) => sum + (Number(s.ctc) || 0), 0);
    const avgCtc = structures.length ? Math.round(totalCtc / structures.length) : 0;
    return {
      packages: structures.length,
      templates: templates.length,
      totalCtc,
      avgCtc,
    };
  }, [structures, templates]);

  const templateFilterId = searchParams.get("template_id") || "";

  const filteredStructures = useMemo(() => {
    let list = structures;
    if (templateFilterId) {
      list = list.filter((s) => String(s.template_id) === String(templateFilterId));
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) => {
      const tpl = templates.find((t) => t.template_id === s.template_id);
      const label = structureEmployeeLabel(s, employees).toLowerCase();
      const tplName = (tpl?.name || "").toLowerCase();
      return label.includes(q) || tplName.includes(q) || String(s.ctc || "").includes(q);
    });
  }, [structures, search, employees, templates, templateFilterId]);

  /** Active employees with no package — these block payroll even if other dates look fine. */
  const employeesMissingPackage = useMemo(() => {
    if (!employees?.length) return [];
    const packaged = new Set(
      (structures || []).map((s) => String(s.employee_id || "")).filter(Boolean)
    );
    return employees.filter((emp) => {
      const id = String(emp?.employee_id || emp?.id || "");
      return id && !packaged.has(id);
    });
  }, [employees, structures]);

  function beginAssignMissing(emp) {
    const id = String(emp?.employee_id || emp?.id || "");
    if (!id) return;
    setEmployeeId(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleAssign() {
    if (!canSave) return;
    if (!isTemplateAssignable(selectedTemplate)) {
      toast.error("Inactive salary structures cannot be assigned to employees.");
      return;
    }
    setSaving(true);
    try {
      const saved = await assignEmployeeSalary({
        employee_id: employeeId,
        template_id: templateId,
        ctc: Number(ctc),
        effective_from: isoDate(effectiveFrom),
      });
      toast.success("Salary package updated for this employee", {
        title: "Saved successfully",
      });
      await Promise.all([loadStructures(), refreshEmployees()]);
      // Keep the employee selected and show the saved values in the form.
      setEmployeeId(String(saved?.employee_id || employeeId));
      setTemplateId(saved?.template_id ? String(saved.template_id) : templateId);
      setCtc(saved?.ctc != null ? String(saved.ctc) : String(ctc));
      setEffectiveFrom(isoDate(saved?.effective_from) || effectiveFrom);
      dateDirty.current = false;
      loadedPackageFor.current = String(saved?.employee_id || employeeId);
      setCtcPrefillSource("package");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to save package");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="w-full max-w-full min-w-0 p-4 md:p-6 space-y-5">
      <HrHero
        eyebrow={
          <span className="inline-flex items-center gap-2">
            <Package size={15} />
            Compensation management
          </span>
        }
        title="Assign salary package"
        subtitle="Link each employee to a salary template and annual CTC. Saving replaces the previous package and drives payroll gross/net calculation."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/salary-v2/setup"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-sm font-bold shadow-[0_8px_24px_-12px_rgba(0,0,0,0.4)] hover:shadow-[0_12px_30px_-12px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 transition"
              style={{ color: "#1D4ED8" }}
              title="New here? Open the Guided Setup Wizard"
            >
              <Rocket size={15} />
              Quick Start Guide
            </Link>
            <HrBtn
              variant="secondary"
              className="!bg-white/15 !border-white/25 !text-white hover:!bg-white/25 backdrop-blur-sm"
              onClick={loadStructures}
              disabled={loadingList}
            >
              <RefreshCw size={15} className={loadingList ? "animate-spin" : ""} />
              Refresh
            </HrBtn>
          </div>
        }
        stats={[
          { label: "Active packages", value: stats.packages },
          { label: "Templates", value: stats.templates },
          { label: "Total annual CTC", value: stats.totalCtc ? formatCurrency(stats.totalCtc) : "—" },
          { label: "Avg package", value: stats.avgCtc ? formatCurrency(stats.avgCtc) : "—" },
        ]}
      />

      <PayrollSetupProgress refreshKey={setupRefreshKey} />

      {!loadingList && !employeesLoading && employeesMissingPackage.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-amber-200 bg-amber-50/90 p-4 md:p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <AlertTriangle size={18} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <p className="text-sm font-bold text-amber-950">
                {employeesMissingPackage.length} active employee
                {employeesMissingPackage.length === 1 ? "" : "s"} still need a salary package
              </p>
              <p className="text-xs text-amber-900/80 mt-1 max-w-3xl">
                Editing Effective From on employees who already have a package does not fix this.
                Payroll needs a template + CTC for every active employee — including those not listed under Current packages.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {employeesMissingPackage.slice(0, 12).map((emp) => {
                  const id = String(emp.employee_id || emp.id);
                  const label = formatEmployeeLabel(emp) || "Employee";
                  const selected = String(employeeId) === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => beginAssignMissing(emp)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        selected
                          ? "border-amber-600 bg-amber-600 text-white"
                          : "border-amber-300 bg-white text-amber-950 hover:bg-amber-100"
                      }`}
                    >
                      <Users size={12} />
                      {label}
                      <ArrowRight size={12} />
                    </button>
                  );
                })}
                {employeesMissingPackage.length > 12 ? (
                  <span className="inline-flex items-center text-xs font-medium text-amber-800">
                    +{employeesMissingPackage.length - 12} more
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}

      {/* First-time onboarding banner — opens Guided Setup Wizard */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative rounded-2xl overflow-hidden border border-indigo-100 shadow-[0_4px_18px_-8px_rgba(79,70,229,0.25)]"
        style={{ background: "linear-gradient(120deg, #EEF2FF 0%, #E0F2FE 55%, #ECFDF5 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br from-amber-300/30 to-pink-300/20 blur-2xl pointer-events-none" />
        <div className="relative flex flex-wrap items-center gap-4 p-4 md:p-5">
          <div className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-500 text-white flex items-center justify-center shadow-md">
            <BookOpen size={20} />
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-extrabold text-indigo-700 bg-white border border-indigo-200 rounded-full px-2 py-0.5 mb-1">
              <Sparkles size={10} className="text-amber-500" />
              First time here?
            </div>
            <p className="text-[14px] md:text-[15px] font-bold text-slate-900">
              New to Salary Engine? Set it up in 5 simple steps.
            </p>
            <p className="text-[12.5px] text-slate-600 mt-0.5 max-w-2xl">
              Payroll Settings → Components → Formulas → Templates → Assign packages. The Guided Setup Wizard walks you through each step.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/salary-v2/setup"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white bg-gradient-to-r from-indigo-500 to-blue-600 shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)] hover:shadow-[0_10px_24px_-6px_rgba(37,99,235,0.55)] hover:-translate-y-0.5 transition"
            >
              <Rocket size={14} />
              Open Quick Start
              <ArrowRight size={13} />
            </Link>
            <Link
              to="/salary-v2/components"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-[12.5px] font-semibold text-indigo-700 hover:bg-white/70 transition"
            >
              Skip to components
              <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.05 }}
      >
        <HrPanel
          title="New assignment"
          subtitle="Select employee, template, CTC, and effective date — current package auto-fills when an employee is chosen."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="sal-form-label">
                <Users size={13} />
                Employee
              </label>
              <EmployeeSelect
                value={employeeId}
                onChange={setEmployeeId}
                employees={employees}
                required
                hideLabel
                selectClassName="hr-input w-full"
              />
              {!employeesLoading && employeesError ? (
                <p className="mt-1.5 text-xs text-amber-700">
                  Could not load employees.{" "}
                  <button type="button" className="underline font-semibold" onClick={refreshEmployees}>
                    Retry
                  </button>
                </p>
              ) : null}
              {!employeesLoading && !employeesError && employees.length === 0 ? (
                <p className="mt-1.5 text-xs text-slate-500">
                  No employees found.{" "}
                  <Link to="/employees/add" className="text-indigo-600 font-semibold hover:underline">
                    Add an employee
                  </Link>{" "}
                  first.
                </p>
              ) : null}
            </div>

            <div>
              <label className="sal-form-label">
                <Layers size={13} />
                Salary template
              </label>
              <HrSelect value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Select template</option>
                {assignableTemplates.map((t) => {
                  const inactive = !isTemplateAssignable(t);
                  return (
                    <option key={t.template_id} value={t.template_id} disabled={inactive}>
                      {t.name}{inactive ? " (Inactive — not assignable)" : ""}
                    </option>
                  );
                })}
              </HrSelect>
              {selectedIsInactive ? (
                <p className="mt-1.5 text-xs text-amber-700">
                  This structure is inactive and cannot be assigned. Choose an active structure, or activate it in 3.3 Salary Structures.
                </p>
              ) : null}
            </div>

            <div>
              <label className="sal-form-label">
                <IndianRupee size={13} />
                Annual CTC
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                <HrInput
                  className="!pl-7"
                  type="number"
                  min="0"
                  placeholder="e.g. 780000"
                  value={ctc}
                  onChange={(e) => {
                    setCtc(e.target.value);
                    setCtcPrefillSource(null);
                  }}
                />
              </div>
              {ctcPrefillSource === "profile" && ctc ? (
                <p className="mt-1.5 text-xs text-teal-700">
                  Loaded from employee profile — no need to re-enter CTC.
                </p>
              ) : null}
              {ctcPrefillSource === "wizard" && ctc ? (
                <p className="mt-1.5 text-xs text-teal-700">
                  Carried over from Add Employee — adjust only if needed.
                </p>
              ) : null}
            </div>

            <div>
              <label className="sal-form-label">
                <CalendarDays size={13} />
                Effective from
              </label>
              <DatePickerField
                hideLabel
                value={effectiveFrom}
                onChange={(next) => {
                  dateDirty.current = true;
                  setEffectiveFrom(next);
                }}
                placeholder="Effective from"
                inputClassName="hr-input w-full"
              />
            </div>
          </div>

          {loadingCurrent && employeeId ? (
            <div className="flex items-center gap-2 mt-4 text-xs text-slate-500">
              <Loader2 size={14} className="animate-spin text-teal-600" />
              Loading current package…
            </div>
          ) : null}

          {(employeeId && (selectedEmployee || selectedTemplate || monthlyCtc)) ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="sal-preview-card mt-5"
            >
              <div className="flex flex-wrap items-center gap-4">
                {selectedEmployee ? (
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="sal-avatar"
                      style={{
                        background: `linear-gradient(135deg, ${avatarHue(formatEmployeeLabel(selectedEmployee))}22, ${avatarHue(formatEmployeeLabel(selectedEmployee))}44)`,
                        color: avatarHue(formatEmployeeLabel(selectedEmployee)),
                      }}
                    >
                      {getInitials(formatEmployeeLabel(selectedEmployee))}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{formatEmployeeLabel(selectedEmployee)}</p>
                      <p className="text-xs text-slate-500">Selected employee</p>
                    </div>
                  </div>
                ) : null}
                {selectedTemplate ? (
                  <div className="sal-preview-chip">
                    <FileText size={13} className="text-indigo-600" />
                    {selectedTemplate.name}
                  </div>
                ) : null}
                {monthlyCtc ? (
                  <div className="sal-preview-chip sal-preview-chip--money">
                    <TrendingUp size={13} />
                    ~{formatCurrency(monthlyCtc)}/mo
                  </div>
                ) : null}
              </div>
            </motion.div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3 mt-6 pt-5 border-t border-slate-100">
            <HrBtn variant="primary" onClick={handleAssign} disabled={!canSave}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              {saving ? "Saving…" : "Save package"}
            </HrBtn>
            {employeeId ? (
              <HrBtn
                variant="ghost"
                onClick={() => {
                  setEmployeeId("");
                  setTemplateId("");
                  setCtc("");
                  setEffectiveFrom(todayIso());
                  setCtcPrefillSource(null);
                }}
              >
                Clear form
              </HrBtn>
            ) : null}
          </div>
        </HrPanel>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <HrPanel
          title="Current packages"
          subtitle={
            filteredStructures.length === structures.length
              ? employeesMissingPackage.length
                ? `One row per employee — ${structures.length} package${structures.length === 1 ? "" : "s"}; ${employeesMissingPackage.length} active employee${employeesMissingPackage.length === 1 ? "" : "s"} still missing`
                : `One row per employee — ${structures.length} active package${structures.length === 1 ? "" : "s"}`
              : `${filteredStructures.length} of ${structures.length} packages match your search`
          }
          headerRight={
            <div className="relative w-full sm:w-56">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <HrInput
                className="!pl-9 !py-2"
                placeholder="Search employee or template…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          }
          noPadding
        >
          <div className="sal-packages-scroll sal-packages-scroll--page">
            {loadingList ? (
              <div className="p-8 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="sal-skeleton h-12 rounded-xl" />
                ))}
              </div>
            ) : filteredStructures.length === 0 ? (
              <HrEmptyState
                icon={Banknote}
                title={search ? "No packages match your search" : "No salary packages yet"}
                description={
                  search
                    ? "Try a different name, template, or CTC value."
                    : "Assign a template and CTC above to create the first employee package."
                }
              />
            ) : (
              <table className="sal-packages-table">
                <thead>
                  <tr>
                    <th className="sal-packages-th">Employee</th>
                    <th className="sal-packages-th">Template</th>
                    <th className="sal-packages-th">Annual CTC</th>
                    <th className="sal-packages-th">Monthly</th>
                    <th className="sal-packages-th">Effective from</th>
                    <th className="sal-packages-th sal-packages-th--action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStructures.map((s, idx) => {
                    const tpl = templates.find((t) => t.template_id === s.template_id);
                    const label = structureEmployeeLabel(s, employees);
                    const hue = avatarHue(label);
                    const annual = Number(s.ctc);
                    const monthly = Number.isFinite(annual) ? Math.round(annual / 12) : null;
                    const isSelected = String(s.employee_id) === String(employeeId);
                    const rowKey = String(s.id || s.employee_id);

                    return (
                      <motion.tr
                        key={rowKey}
                        className={`sal-packages-row group${isSelected ? " sal-packages-row--active" : ""}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: Math.min(idx * 0.03, 0.35) }}
                        onClick={() => beginEditPackage(s)}
                        title="Click to edit this package"
                      >
                        <td className="sal-packages-td">
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="sal-avatar shrink-0"
                              style={{
                                background: `linear-gradient(135deg, ${hue}22, ${hue}44)`,
                                color: hue,
                              }}
                            >
                              {getInitials(label)}
                            </div>
                            <span className="font-semibold text-slate-900 truncate">{label}</span>
                          </div>
                        </td>
                        <td className="sal-packages-td">
                          <span className="sal-template-pill">
                            <FileText size={12} />
                            {tpl?.name || "—"}
                          </span>
                        </td>
                        <td className="sal-packages-td">
                          <span className="sal-ctc-value">{formatCurrency(s.ctc)}</span>
                        </td>
                        <td className="sal-packages-td">
                          <span className="text-sm text-slate-600 tabular-nums">
                            {monthly ? formatCurrency(monthly) : "—"}
                          </span>
                        </td>
                        <td className="sal-packages-td">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-slate-800">{formatDate(s.effective_from)}</span>
                            <span className="text-[10px] text-slate-400 tabular-nums">{s.effective_from}</span>
                          </div>
                        </td>
                        <td className="sal-packages-td sal-packages-td--action">
                          <button
                            type="button"
                            className="sal-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              beginEditPackage(s);
                            }}
                          >
                            <Pencil size={13} />
                            Edit
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </HrPanel>
      </motion.div>
    </div>
  );
}
